/**
 * The six numbers that answer "what happened in this run".
 *
 * Every figure is rendered exactly as the backend supplied it. The only
 * arithmetic here is over the statement ARRAYS (adding up supplied fields),
 * which is a rollup of data the payload already carries, not a re-derivation of
 * a backend number.
 *
 * `checks_run` is shown as a plain count and nothing more. The payload has no
 * per-rule pass list behind it, so anything shaped like "27 of 30 checks
 * passed" would be inventing the denominator.
 */

import {
  AlertTriangle,
  CalendarRange,
  Database,
  Gauge,
  ListChecks,
  Sparkles,
} from 'lucide-react';
import StatTile from '../../ui/StatTile';
import type { ValidationSummary } from '../../../types/dataQuality';
import { formatQualityScore, resolveScore } from '../utils/dataQualityFormat';
import { supplyTotals } from './dqSelectors';
import { AI_STATUS_TOKENS, scoreBand } from './dqTokens';

export interface AssuranceTilesProps {
  summary: ValidationSummary;
}

export default function AssuranceTiles({ summary }: AssuranceTilesProps) {
  const { score, isAgentAdjusted } = resolveScore(summary);
  const band = scoreBand(score);
  const supply = supplyTotals(summary.statements);

  const errors = summary.error_count ?? 0;
  const warnings = summary.warning_count ?? 0;
  // `info_count` is typed and read here, but the backend's `ui_summary()`
  // digest does not currently emit it — and its `actionable()` filter keeps
  // `info` findings out of `issues[]` too. So this is 0 in practice today; the
  // tile is labelled "actionable" rather than silently under-reporting a total.
  const notes = summary.info_count ?? 0;
  const actionable = errors + warnings;

  const ai = summary.ai_status ? AI_STATUS_TOKENS[summary.ai_status] : null;
  const coverage = summary.agent_coverage;
  const hasCoverage = coverage != null && Number.isFinite(coverage);

  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-6">
      <StatTile
        label="Quality score"
        value={formatQualityScore(score) || '—'}
        sub={
          <span className={band.text}>
            {band.label}
            {isAgentAdjusted ? ' · after AI review' : ''}
          </span>
        }
        icon={<Gauge className="h-4 w-4" />}
        iconBg="bg-secondary-50"
        iconColor="text-secondary-700"
        valueClassName="font-mono tabular-nums"
      />

      <StatTile
        label="Actionable findings"
        value={actionable}
        sub={
          actionable === 0
            ? 'nothing raised'
            : [
                errors ? `${errors} error${errors === 1 ? '' : 's'}` : null,
                warnings ? `${warnings} warning${warnings === 1 ? '' : 's'}` : null,
                notes ? `+ ${notes} note${notes === 1 ? '' : 's'}` : null,
              ]
                .filter(Boolean)
                .join(' · ')
        }
        icon={<AlertTriangle className="h-4 w-4" />}
        iconBg={errors > 0 ? 'bg-red-50' : warnings > 0 ? 'bg-amber-50' : 'bg-emerald-50'}
        iconColor={errors > 0 ? 'text-red-600' : warnings > 0 ? 'text-amber-600' : 'text-emerald-600'}
        valueClassName="font-mono tabular-nums"
      />

      <StatTile
        label="Distinct issues"
        value={summary.issue_count ?? 0}
        // The count that stops "10 warnings" reading as ten problems.
        sub="one per kind of problem"
        icon={<ListChecks className="h-4 w-4" />}
        iconBg="bg-primary-50"
        iconColor="text-primary-600"
        valueClassName="font-mono tabular-nums"
      />

      <StatTile
        label="Periods checked"
        value={summary.periods_checked ?? 0}
        sub={`${summary.checks_run ?? 0} checks run`}
        icon={<CalendarRange className="h-4 w-4" />}
        iconBg="bg-primary-50"
        iconColor="text-primary-600"
        valueClassName="font-mono tabular-nums"
      />

      <StatTile
        label="Fields supplied"
        value={supply.reported + supply.derived}
        // `statements[]` describes the NEWEST period only, so the qualifier is
        // not decoration — without it this reads as an all-years total.
        sub={
          supply.defaulted > 0
            ? `${supply.defaulted} defaulted to zero · latest period`
            : `across ${supply.availableCount} statement${
                supply.availableCount === 1 ? '' : 's'
              } · latest period`
        }
        icon={<Database className="h-4 w-4" />}
        iconBg={supply.defaulted > 0 ? 'bg-amber-50' : 'bg-emerald-50'}
        iconColor={supply.defaulted > 0 ? 'text-amber-600' : 'text-emerald-600'}
        valueClassName="font-mono tabular-nums"
      />

      <StatTile
        label="AI review"
        value={ai ? ai.label.replace(/^AI /, '') : '—'}
        sub={
          hasCoverage && summary.ai_status === 'completed'
            ? `${Math.round((coverage as number) * 100)}% of findings reviewed`
            : ai?.tone === 'degraded'
              ? 'score is rule-based only'
              : summary.ai_model || summary.ai_provider || 'rule-based score'
        }
        icon={<Sparkles className="h-4 w-4" />}
        iconBg={ai?.tone === 'degraded' ? 'bg-amber-50' : 'bg-secondary-50'}
        iconColor={ai?.tone === 'degraded' ? 'text-amber-600' : 'text-secondary-700'}
        valueClassName="text-base"
      />
    </div>
  );
}
